import { BRAZILIAN_UF_VALUES } from '@bcube/shared';
import { z } from 'zod/v3';

/** Brazilian UF enum */
export const BRAZILIAN_UF_ENUM = z.enum(BRAZILIAN_UF_VALUES);

export type BrazilianUF = z.infer<typeof BRAZILIAN_UF_ENUM>;

/** PT-BR money regex: 1.234,56 format */
const PTBR_MONEY_REGEX = /^(\d{1,3}(\.\d{3})*|\d+)(,\d{2})?$/;

/** Condition per group */
const priceTableConditionSchema = z.object({
  id: z.string().min(1),
  /** UFs served by this condition */
  salesFor: z.array(BRAZILIAN_UF_ENUM).optional(),
  /** Billing limit (pt-BR string) */
  billingLimitInCents: z
    .string()
    .trim()
    .optional()
    .refine((v) => !v || PTBR_MONEY_REGEX.test(v), 'Informe um valor válido (ex.: 1.234,56)'),
  /** Who to bill (id/option) */
  toBillFor: z.string().min(1, 'Selecione para quem faturar'),
  /** ICMS contributor: true = Sim, false = Não, null/undefined = sem seleção */
  icmsContributor: z.boolean().nullable().optional(),
});

/** Condition group */
const priceTableConditionGroupSchema = z.object({
  id: z.string().min(1),
  /** Enable priority in group */
  priority: z.boolean().optional(),
  /** Conditions list */
  conditions: z.array(priceTableConditionSchema).min(1, 'Adicione pelo menos uma condição'),
});

/** Main price table schema */
export const priceTableSchema = z
  .object({
    name: z
      .string()
      .min(1, 'Nome da tabela é obrigatório')
      .max(100, 'Nome muito longo (máximo 100 caracteres)'),
    startDateTime: z.date({
      required_error: 'Data e hora de início são obrigatórias',
      invalid_type_error: 'Data inválida',
    }),
    endDateTime: z
      .date({
        invalid_type_error: 'Data inválida',
      })
      .optional(),
    isTemporary: z.boolean().default(false),

    groups: z.array(priceTableConditionGroupSchema).default([]),

    // Billing configuration - billTo is optional, validation in individual conditions
    billingConfig: z.object({
      salesFor: z.string().optional(),
      billingLimitInCents: z.string().optional(),
      billTo: z.string().optional(),
    }),
    equipmentWithSim: z.record(z.any()).default({}),
    equipmentWithoutSim: z.record(z.any()).default({}),
    simCards: z.array(z.any()).default([]),
    accessories: z.record(z.any()).default({}),
    services: z.array(z.any()).default([]),
  })
  .refine(
    (data) => {
      return data.startDateTime > new Date();
    },
    {
      message: 'A data e hora de início devem ser no futuro',
      path: ['startDateTime'],
    },
  )
  .refine(
    (data) => {
      if (data.isTemporary) {
        if (!data.endDateTime) {
          return false;
        }
        return data.endDateTime > data.startDateTime;
      }
      return true;
    },
    {
      message:
        'Data de fim é obrigatória e deve ser posterior à data de início para tabelas temporárias',
      path: ['endDateTime'],
    },
  )
  .refine(
    (data) => {
      // Ensure there's at least one group with at least one condition that has toBillFor
      const hasValidBillingCondition = data.groups.some((group) =>
        group.conditions.some(
          (condition) => condition.toBillFor && condition.toBillFor.trim().length > 0,
        ),
      );
      return hasValidBillingCondition;
    },
    {
      message: "Pelo menos uma condição deve ter o campo 'Faturar para' preenchido",
      path: ['groups'],
    },
  );

export type CreatePriceTableFormData = z.infer<typeof priceTableSchema>;

/** Price tier schema for equipment/accessory pricing */
const integerField = z
  .string()
  .min(1, 'Obrigatório')
  .regex(/^\d+$/, 'Use apenas números inteiros')
  .transform((value) => parseInt(value, 10))
  .refine((value) => value >= 0, {
    message: 'Deve ser maior ou igual a 0',
  });

/** "to" field: accepts integers or the open-range sentinel "__open__" (→ null in DB) */
const toField = z
  .string()
  .min(1, 'Obrigatório')
  .transform((value) => {
    if (value === '__open__') return null;
    return parseInt(value, 10);
  })
  .refine((value) => value === null || (Number.isInteger(value) && value >= 0), {
    message: 'Use apenas números inteiros',
  });

const monetaryField = z
  .string()
  .min(1, 'Obrigatório')
  .transform((value) => Number(value.replace(/\./g, '').replace(',', '.')))
  .refine((value) => Number.isFinite(value), {
    message: 'Número inválido',
  })
  .refine((value) => value > 0, {
    message: 'Deve ser maior que 0',
  });

export const priceTierSchema = z.object({
  from: integerField,
  to: toField,
  unitPrice: monetaryField,
});

export type PriceTier = z.infer<typeof priceTierSchema>;

const GLOBAL_ERROR_MESSAGE = 'Ranges must be contiguous, non-overlapping, and [from,to).';

export const priceTierArraySchema = z
  .array(priceTierSchema)
  .min(1, 'Informe pelo menos uma faixa')
  .superRefine((tiers, ctx) => {
    for (let index = 0; index < tiers.length; index += 1) {
      const current = tiers[index];
      const isLast = index === tiers.length - 1;

      // Last tier has open-ended range (to === null), skip from < to check
      if (!isLast && current.to !== null && current.from > current.to) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: '"Até" deve ser maior ou igual a "De"',
          path: [index, 'to'],
        });
      }

      if (index === 0) {
        continue;
      }

      const previous = tiers[index - 1];

      // Can't check contiguity if previous tier has open-ended range
      if (previous.to === null) continue;

      if (current.from !== previous.to + 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: GLOBAL_ERROR_MESSAGE,
          path: [],
        });
        break;
      }
    }
  });

export const priceTierFormSchema = z.object({
  tiers: priceTierArraySchema,
});

export type PriceTierFormValues = z.input<typeof priceTierFormSchema>;
